import type {
  Email,
  Fax,
  Phone,
  PointOfContact,
  PointOfContactChangeCommandData,
  PointOfContactVersion,
} from '@festina/legalentity-general-types/legalentity'
import {
  type ExcludeString,
  type ExcludeStringLiteral,
  notEmpty,
  stringUtils,
  typeUtils,
} from '@festina-life/core'
import {
  FormikDateInput,
  FormikFieldArray,
  FormikForm,
} from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import * as Yup from 'yup'

import { useLegalentityLatestEntityVersion } from '../../../hooks/useLegalentityLatestEntityVersion'
import type { CommandDataFormProps } from '../../commandActions/CommandDataFormAndSimulation'
import { PointOfContactFields } from './PointOfContactFields'
import { isEmailVersion, isFaxVersion, isPhoneVersion } from './utilts'

const validateKey = typeUtils.createValidator<PointOfContactChangeCommandData>()

type Props = CommandDataFormProps<PointOfContactChangeCommandData>

export function PointOfContactChangeForm(props: Props) {
  const translate = useTranslate()

  const entityVersion = useLegalentityLatestEntityVersion()
  const { pointOfContactVersions } = entityVersion ?? {}

  const initialPointOfContacts: PointOfContact[] =
    pointOfContactVersions
      ?.map((x) => {
        if (
          x?._type &&
          (stringUtils.strictEqual(x._type, 'EmailVersion') ||
            stringUtils.strictEqual(x._type, 'FaxVersion') ||
            stringUtils.strictEqual(x._type, 'PhoneVersion'))
        ) {
          const pointOfContactTypeMap = {
            EmailVersion: {
              _type: 'Email',
              email: isEmailVersion(x) ? x.email : undefined,
            } satisfies Email,
            FaxVersion: {
              _type: 'Fax',
              fax: isFaxVersion(x) ? x.fax : undefined,
            } satisfies Fax,
            PhoneVersion: {
              _type: 'Phone',
              phone: isPhoneVersion(x) ? x.phone : undefined,
            } satisfies Phone,
          } satisfies Record<
            ExcludeStringLiteral<
              ExcludeString<PointOfContactVersion['_type']>,
              'PointOfContactVersion'
            >,
            Email | Fax | Phone
          >

          const pointOfContactType = pointOfContactTypeMap[x._type]

          return {
            ...pointOfContactType,
            isValid: x.isValid,
          } satisfies PointOfContact
        } else {
          return
        }
      })
      .filter(notEmpty) ?? []

  const initialValues: PointOfContactChangeCommandData = {
    _type: 'PointOfContactChangeCommandData',
    pointOfContacts: initialPointOfContacts,
  }

  const validationSchema = Yup.object({
    pointOfContacts: Yup.array().of(
      Yup.object({
        _type: Yup.string().required(translate('validation.required')),
        email: Yup.string().when('_type', {
          is: 'Email',
          then: (schema) =>
            schema
              .email(translate('validation.email'))
              .required(translate('validation.required')),
        }),
        fax: Yup.string().when('_type', {
          is: 'Fax',
          then: (schema) => schema.required(translate('validation.required')),
        }),
        phone: Yup.string().when('_type', {
          is: 'Phone',
          then: (schema) => schema.required(translate('validation.required')),
        }),
        isValid: Yup.boolean(),
      }),
    ),
  })

  return (
    <FormikForm
      initialValues={initialValues}
      validationSchema={validationSchema}
      {...props}
    >
      <FormikDateInput
        name={validateKey('changeDate')}
        label={translate('pointOfContact.field.changeDate')}
      />
      <FormikFieldArray
        name={validateKey('pointOfContacts')}
        label={translate('pointOfContact.field.pointOfContacts')}
        elementLabel={translate('pointOfContact.field.pointOfContact')}
        elementInitializer={() =>
          ({ _type: 'Email', isValid: false }) satisfies Email
        }
        ChildElement={PointOfContactFields}
      />
    </FormikForm>
  )
}
